import { useMemo, useState } from "react"
import { Box, Button, HStack, Spinner, Stack } from "@chakra-ui/react"
import { Text } from "@/components"
import type { IrrFundDashboard, IrrDealFund } from "@/types"

// ── Formatters ──────────────────────────────────────────────────────────────
const fmtMoneyFull = (v: number | null | undefined) =>
  v == null
    ? "—"
    : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtPct = (v: number | null | undefined) =>
  v == null ? "—" : `${(v * 100).toFixed(2)}%`
const fmtX = (v: number | null | undefined) =>
  v == null ? "—" : `${v.toFixed(3)}x`
const fmtInt = (v: number | null | undefined) =>
  v == null ? "—" : v.toLocaleString()

// ── Quarter helpers ─────────────────────────────────────────────────────────
function getQuarterRange(quarter: number, year: number) {
  const monthStart = (quarter - 1) * 3
  const start = `${year}-${String(monthStart + 1).padStart(2, "0")}-01`
  const endMonth = monthStart + 3
  const endYear = endMonth > 12 ? year + 1 : year
  const endM = endMonth > 12 ? 1 : endMonth
  // End date is the day before the first of next quarter
  const endDate = new Date(endYear, endM - 1, 0)
  const end = `${endYear}-${String(endDate.getMonth() + 1).padStart(2, "0")}-${String(endDate.getDate()).padStart(2, "0")}`
  return { start, end }
}

function isInRange(dateStr: string | null | undefined, start: string, end: string): boolean {
  if (!dateStr) return false
  const d = dateStr.slice(0, 10)
  return d >= start && d <= end
}

// ── Section component ───────────────────────────────────────────────────────
const ReportSection = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <Box
    border="1px solid"
    borderColor="border.default"
    borderRadius="md"
    overflow="hidden"
  >
    <Box
      bg="gray.50"
      _dark={{ bg: "gray.800" }}
      px={4}
      py={2}
      borderBottom="1px solid"
      borderColor="border.default"
    >
      <Text fontSize="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.06em">
        {title}
      </Text>
    </Box>
    <Box px={4} py={3}>
      {children}
    </Box>
  </Box>
)

const MetricRow = ({
  label,
  value,
  bold,
  indent,
}: {
  label: string
  value: string
  bold?: boolean
  indent?: boolean
}) => (
  <HStack
    justifyContent="space-between"
    py={1}
    borderBottom="1px solid"
    borderColor="border.default"
    _last={{ borderBottom: "none" }}
  >
    <Text
      fontSize="13px"
      color={indent ? "text.muted" : undefined}
      pl={indent ? 4 : 0}
      fontWeight={bold ? "semibold" : "normal"}
    >
      {label}
    </Text>
    <Text
      fontSize="13px"
      fontWeight={bold ? "semibold" : "normal"}
      fontVariantNumeric="tabular-nums"
    >
      {value}
    </Text>
  </HStack>
)

const CompanyListRow = ({ label, companies }: { label: string; companies: string[] }) => (
  <Box py={1} borderBottom="1px solid" borderColor="border.default" _last={{ borderBottom: "none" }}>
    <Text fontSize="13px" fontWeight="medium" mb={0.5}>
      {label}
    </Text>
    <Text fontSize="12px" color="text.muted" lineHeight="1.5">
      {companies.length > 0 ? companies.join(", ") : "None"}
    </Text>
  </Box>
)

// ── Main component ──────────────────────────────────────────────────────────
interface Props {
  funds: IrrFundDashboard[]
  irrDealFunds: IrrDealFund[]
  quarter: number
  year: number
  isLoading: boolean
}

export const IrrQuarterlyReportTab = ({
  funds,
  irrDealFunds,
  quarter,
  year,
  isLoading,
}: Props) => {
  const [selectedFundId, setSelectedFundId] = useState<number>(funds[0]?.fundId ?? 1)
  const fund = funds.find((f) => f.fundId === selectedFundId) ?? funds[0]

  // Get deals for the selected fund
  const fundDeals = useMemo(() => {
    const fundName = fund?.fundName
    if (!fundName) return []
    const group = irrDealFunds.find((f) => f.whichFund === fundName)
    return group?.deals ?? []
  }, [fund?.fundName, irrDealFunds])

  const { start: qStart, end: qEnd } = useMemo(
    () => getQuarterRange(quarter, year),
    [quarter, year]
  )

  // ── Quarterly computations ──────────────────────────────────────────────
  const metrics = useMemo(() => {
    if (!fund || fundDeals.length === 0) return null

    // Active deals: not fully exited (residualValue > 0)
    const activeDeals = fundDeals.filter(
      (d) => d.fullyExitedPortfolio !== "Yes"
    )
    const activeCompanies = new Set(activeDeals.map((d) => d.companyId))

    // New deals this quarter: closingDate in [qStart, qEnd]
    const newDeals = fundDeals.filter((d) =>
      isInRange(d.originalClosingDate, qStart, qEnd)
    )
    const newDealInvestment = newDeals.reduce(
      (sum, d) => sum + parseFloat(d.esoTotalPayment || "0"),
      0
    )

    // Figure out which companies are "new" (first deal ever is in this quarter)
    // vs "existing" (had a deal before this quarter)
    const companiesWithPriorDeals = new Set(
      fundDeals
        .filter((d) => d.originalClosingDate && d.originalClosingDate.slice(0, 10) < qStart)
        .map((d) => d.companyName)
    )
    const newCompanies = [
      ...new Set(
        newDeals
          .filter((d) => !companiesWithPriorDeals.has(d.companyName))
          .map((d) => d.companyName)
      ),
    ].sort()
    const existingCompanies = [
      ...new Set(
        newDeals
          .filter((d) => companiesWithPriorDeals.has(d.companyName))
          .map((d) => d.companyName)
      ),
    ].sort()

    // Realization events this quarter: deals with positive cash flows in the period.
    // Excel counts rows in the "Realization Events" sheet — each row is an individual
    // cash receipt. In v3, positive IrrCashFlow entries are the equivalent.
    const dealsWithRealizations = new Map<string, { companyName: string; basis: number }>()
    let realizationEventCount = 0
    let realizationAmount = 0
    for (const d of fundDeals) {
      for (const cf of d.cashFlows) {
        if (cf.amount > 0 && isInRange(cf.date, qStart, qEnd)) {
          realizationEventCount++
          realizationAmount += cf.amount
          if (!dealsWithRealizations.has(d.dealId)) {
            dealsWithRealizations.set(d.dealId, {
              companyName: d.companyName,
              basis: parseFloat(d.esoTotalPayment || "0"),
            })
          }
        }
      }
    }
    const exitBasis = Array.from(dealsWithRealizations.values()).reduce(
      (sum, d) => sum + d.basis,
      0
    )
    const exitRoi = exitBasis > 0 ? realizationAmount / exitBasis : null
    const exitCompanies = [
      ...new Set(Array.from(dealsWithRealizations.values()).map((d) => d.companyName)),
    ].sort()

    // ── Performance Data (from fund dashboard) ────────────────────────────
    const paidIn = fund.totalPaidIn
    const distributions = fund.totalDistributions ?? 0
    const grossTotalValue = fund.totalValue
    const grossRvpi =
      paidIn && paidIn > 0 ? fund.totalHoldingValue / paidIn : null
    const carriedInterestAccrued =
      paidIn != null ? Math.max(0, (grossTotalValue - paidIn) * 0.2) : null
    const carriedInterestPaid = fund.realizedGpCarry ?? 0

    // Net metrics
    const netDistributions = distributions - carriedInterestPaid
    const netDpi =
      paidIn && paidIn > 0 ? netDistributions / paidIn : null
    const netTotalValue =
      carriedInterestAccrued != null
        ? grossTotalValue - carriedInterestAccrued
        : null
    const netTvpi =
      netTotalValue != null && paidIn && paidIn > 0
        ? netTotalValue / paidIn
        : null
    const unrealizedCarry =
      carriedInterestAccrued != null
        ? Math.max(0, carriedInterestAccrued - carriedInterestPaid)
        : null
    const netRvpi =
      grossRvpi != null && unrealizedCarry != null && paidIn && paidIn > 0
        ? grossRvpi - unrealizedCarry / paidIn
        : null

    // LRP supplemental
    const lrpTotalValue =
      fund.totalResidualAtLrp + distributions
    const lrpCarryAccrued =
      paidIn != null ? Math.max(0, (lrpTotalValue - paidIn) * 0.2) : null
    const netTotalValueLrp =
      lrpCarryAccrued != null ? lrpTotalValue - lrpCarryAccrued : null
    const netTvpiLrp =
      netTotalValueLrp != null && paidIn && paidIn > 0
        ? netTotalValueLrp / paidIn
        : null
    const lrpUnrealizedCarry =
      lrpCarryAccrued != null
        ? Math.max(0, lrpCarryAccrued - carriedInterestPaid)
        : null
    const netRvpiLrp =
      lrpUnrealizedCarry != null && paidIn && paidIn > 0
        ? (fund.totalResidualAtLrp - lrpUnrealizedCarry) / paidIn
        : null

    return {
      // Section 2
      activeDeals: activeDeals.length,
      activeCompanies: activeCompanies.size,
      // Section 3
      newDealCount: newDeals.length,
      newDealInvestment,
      realizationEventCount,
      realizationAmount,
      exitBasis,
      exitRoi,
      newCompanies,
      existingCompanies,
      exitCompanies,
      // Section 4
      grossRvpi,
      carriedInterestAccrued,
      carriedInterestPaid,
      netDistributions,
      netDpi,
      netTotalValue,
      netTvpi,
      netRvpi,
      // Section 5
      netTotalValueLrp,
      netTvpiLrp,
      netRvpiLrp,
    }
  }, [fund, fundDeals, qStart, qEnd])

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={12}>
        <Spinner size="lg" />
      </Box>
    )
  }

  if (!fund) {
    return <Text color="text.muted">No fund data available.</Text>
  }

  return (
    <Stack gap={5}>
      {/* Header */}
      <HStack justifyContent="space-between" alignItems="center">
        <Text fontSize="lg" fontWeight="semibold">
          Quarterly Report — Q{quarter} {year}
        </Text>
      </HStack>

      {/* Fund selector */}
      <HStack gap={2} flexWrap="wrap">
        {funds.map((f) => (
          <Button
            key={f.fundId}
            size="sm"
            variant={selectedFundId === f.fundId ? "solid" : "outline"}
            onClick={() => setSelectedFundId(f.fundId)}
          >
            {f.fundName}
          </Button>
        ))}
      </HStack>

      {!metrics ? (
        <Text color="text.muted">No deal data available for this fund.</Text>
      ) : (
        <Stack gap={4}>
          {/* Section 1: Since Inception */}
          <ReportSection title="Since Inception">
            <MetricRow label="Number of Investments" value={fmtInt(fund.dealCount)} />
            <MetricRow label="Number of Companies" value={fmtInt(fund.companyCount)} />
          </ReportSection>

          {/* Section 2: Quarterly Snapshot */}
          <ReportSection title={`Quarterly Snapshot — Q${quarter} ${year}`}>
            <MetricRow label="Active Investments" value={fmtInt(metrics.activeDeals)} />
            <MetricRow label="Active Companies" value={fmtInt(metrics.activeCompanies)} />
          </ReportSection>

          {/* Section 3: Quarterly Activity */}
          <ReportSection title={`Quarterly Activity — Q${quarter} ${year}`}>
            <MetricRow label="Number of New Deals" value={fmtInt(metrics.newDealCount)} />
            <MetricRow label="$ of Investments" value={fmtMoneyFull(metrics.newDealInvestment)} />
            <MetricRow label="Realization Events" value={fmtInt(metrics.realizationEventCount)} />
            <MetricRow label="$ Realizations" value={fmtMoneyFull(metrics.realizationAmount)} />
            <MetricRow label="Basis of Realizations" value={fmtMoneyFull(metrics.exitBasis)} />
            <MetricRow
              label="ROI of All Realizations"
              value={metrics.exitRoi != null ? `${metrics.exitRoi.toFixed(2)}x` : "N/A"}
            />
            <CompanyListRow label="New Companies Funded" companies={metrics.newCompanies} />
            <CompanyListRow label="Existing Companies Funded" companies={metrics.existingCompanies} />
            <CompanyListRow label="Companies with Exits This Quarter" companies={metrics.exitCompanies} />
          </ReportSection>

          {/* Section 4: Performance Data */}
          <ReportSection title="Performance Data">
            <MetricRow
              label="Total Committed Capital"
              value={fund.committedCapital != null ? fmtMoneyFull(fund.committedCapital) : "—"}
            />
            <MetricRow label="Total Paid-in Capital" value={fmtMoneyFull(fund.totalPaidIn)} />
            <MetricRow label="Total Invested Capital" value={fmtMoneyFull(fund.totalInvested)} />

            <Box h={2} />
            <MetricRow label="Gross Total Distributions" value={fmtMoneyFull(fund.totalDistributions)} bold />
            <MetricRow label="Gross DPI" value={fmtX(fund.dpi)} indent />
            <MetricRow label="Net Total Distributions" value={fmtMoneyFull(metrics.netDistributions)} bold />
            <MetricRow label="Net DPI" value={fmtX(metrics.netDpi)} indent />

            <Box h={2} />
            <MetricRow label="Gross Total Value" value={fmtMoneyFull(fund.totalValue)} bold />
            <MetricRow label="Gross TVPI" value={fmtX(fund.tvpi)} indent />
            <MetricRow label="Gross RVPI" value={fmtX(metrics.grossRvpi)} indent />
            <MetricRow label="Gross IRR" value={fmtPct(fund.fundIrr)} bold />

            <Box h={2} />
            <MetricRow
              label="Net Total Value"
              value={metrics.netTotalValue != null ? fmtMoneyFull(metrics.netTotalValue) : "—"}
              bold
            />
            <MetricRow label="Net TVPI" value={fmtX(metrics.netTvpi)} indent />
            <MetricRow label="Net RVPI" value={fmtX(metrics.netRvpi)} indent />
            <MetricRow label="Net IRR" value="—" indent />

            <Box h={2} />
            <MetricRow
              label="Carried Interest Accrued"
              value={metrics.carriedInterestAccrued != null ? fmtMoneyFull(metrics.carriedInterestAccrued) : "—"}
            />
            <MetricRow label="Carried Interest Paid" value={fmtMoneyFull(metrics.carriedInterestPaid)} />
          </ReportSection>

          {/* Section 5: Supplemental Non-GAAP (LRP) */}
          <ReportSection title="Supplemental Non-GAAP Information (LRP-Based)">
            <MetricRow
              label="Net Total Value @ LRP"
              value={metrics.netTotalValueLrp != null ? fmtMoneyFull(metrics.netTotalValueLrp) : "—"}
            />
            <MetricRow label="Net TVPI @ LRP" value={fmtX(metrics.netTvpiLrp)} />
            <MetricRow label="Net RVPI @ LRP" value={fmtX(metrics.netRvpiLrp)} />
          </ReportSection>
        </Stack>
      )}
    </Stack>
  )
}
